// collector/invoiceQueue.js
//
// «سجّل وخلّص المحل» with no signal (collector-offline P1-A): the shop's
// invoice is WRITTEN TO THE PHONE first and sent by itself when our server
// answers — like every tap, but on its own queue, because it is not a tap: it
// is one figure per shop (the paper's total, or «المحل مادّاش فاتورة»), a refile
// replaces it, and its paper's photo rides the same request.
//
//   file({ planId, supplierId, amountCents, noPaper, blob })
//                     write the filing to the phone, then ask for a send;
//                     resolves once it is on disk (or after WRITE_WAIT_MS)
//   queued            what the screens show: one entry per filing still waiting
//   pendingCount      how many wait (the sync pill)
//   flush()           send what waits
//
// ON THE PHONE (collector/db.js, the `photos` store): ONE row per shop, the
// row its paper already had — key `invoice-<plan>-<supplier>`. `held` = only a
// paper, nothing filed; `queued` = a filing waiting here. No new store, no
// version bump. A filing is named by its `filedAt`: a refile is a new `filedAt`
// on the same row, and every decision about "the filing that was sent" compares
// it INSIDE the storage transaction (`patchPhoto`), so a refile made while the
// older copy is on the wire — here or in the other container, the Chrome tab
// and the installed app share this storage — is never removed by that copy's
// answer. It stays, and goes next.
//
// WHEN IT SENDS: his marks first. «المحل مادّاش فاتورة» stores his computed
// spend at the moment the request ARRIVES, so the taps of that shop must be on
// the server before it. Nothing goes while a tap of his waits — in this
// memory, being written, or on the phone from the other container (the action
// layer's `tapsWaiting()` reads the phone before every send) — or while the
// login is out. One filing at a time, oldest first, under its own cross-tab
// lock, inside a reload hold. The triggers are
// the photo queue's: the taps draining, the login coming back, the detector
// flipping to online, the page becoming visible, `pageshow`, and a 60 s tick
// while anything waits (the shared detector is kept running meanwhile). The
// tick, the login coming back and the taps draining wait out the backoff; the
// rest try at once.
//
// WHAT AN ANSWER DOES (the contract is the backend's:
// ../backend/app/Http/Controllers/Api/V1/Collectors/CollectorShopInvoiceController.php
// — the reasons are read from the answer, never listed here):
//   applied                       the row goes, its paper with it; the day is re-read
//   conflict, leader_closed       the row goes; ONE card — the leader's figure stands
//   refused, or our API's own     the filing leaves the queue; a row with a paper
//     404 / 422                   goes back to `held`, one without is deleted; ONE
//                                 card carrying the reason and what he had filed
//   our API's 403                 another account signed in on this phone: pause on
//                                 the login and keep it. The same collector: refused
//   401 / 419                     pause on the login, back off, ask the session, keep it
//   no answer, a timeout, a 5xx,  keep it and back off 5 → 10 → 20 → 60 s — never
//     408, 429, a 4xx that is     dropped: a proxy's page or a captive portal's 403
//     not our API's JSON, a 2xx   is the network talking, not a verdict on his invoice.
//     with no `result`            With no answer at all the round stops there; an
//                                 HTTP answer that is not a verdict keeps THAT
//                                 filing and lets the shops filed after it go.
// A refusal also toasts, once, like a refused tap — the card is what survives a
// phone in a pocket.
// The card is written BEFORE the row is changed: a crash between the two sends
// the filing again (the backend rewrites the same row, so a resend is safe)
// instead of losing a refusal he was never told about.
//
// A PINIA STORE for the same reason as the action layer and the photo queue:
// it must outlive the sheet that filed (F7 removes the previous page on every
// push). It joins `actions.js` and `photoQueue.js`, which import each other:
// what is imported from them is read inside functions only, never at module
// load.
import { f7 } from 'framework7-vue';
import { defineStore } from 'pinia';
import { computed, onScopeDispose, ref, shallowRef, watch } from 'vue';
import { BACKOFF_MS, TICK_MS, WRITE_WAIT_MS, isOurApi, useCollectorActionsStore } from '@/collector/actions.js';
import {
  PHOTO_HELD,
  PHOTO_KIND,
  PHOTO_QUEUED,
  collectorStorage,
  filedJustAfter,
  invoiceRowKey,
  sameId,
} from '@/collector/db.js';
import { collectorNetwork } from '@/collector/network.js';
import {
  authPaused,
  collectorUserId,
  normalizeUserId,
  pauseForAuth,
  registerLocalWork,
  sessionAlive,
} from '@/collector/session.js';
import { usePwa } from '@/composables/usePwa';
import i18n from '@/i18n';
import { postCollectorShopInvoice } from '@/services/collectorActions.service.js';
import { probeSession } from '@/services/collectorDay.service.js';
import { shopKey, useCollectorDayStore } from '@/stores/collectorDayStore.js';

const INVOICE_LOCK = 'poolit-collector-invoices';
/** When another tab holds the lock, look again after this. */
const LOCK_RETRY_MS = 3_000;
/** A phone that could not be read at start is asked again this many times, LOCK_RETRY_MS apart. */
const START_RETRIES = 3;
/**
 * Triggers that also look at the phone when memory holds nothing: a filing made
 * in the other container (the installed app, the Chrome tab) is this one's to
 * send too once that one is closed.
 */
const PEEK_REASONS = new Set(['start', 'visible', 'pageshow', 'online', 'resume']);

/** The card states this queue makes (CollectorConflictCards words them). */
export const INVOICE_CARD = Object.freeze({
  LEADER_CLOSED: 'invoice_leader_closed',
  REFUSED: 'invoice_refused',
});

/**
 * The words of a refused filing — the toast here and the card's title on the
 * day page (CollectorConflictCards) say the same thing: the server's reason
 * when the staff copy has it (`collector.doneSheet.refused.<reason>`), else
 * «فاتورة {shop} ماتسجلتش».
 *
 * @param {{ reason?: string|null, shopName?: string|null }} card
 * @param {Function} t - vue-i18n `t`
 * @param {Function} te - vue-i18n `te`
 */
export function refusedInvoiceTitle(card, t, te) {
  const key = `collector.doneSheet.refused.${card?.reason}`;
  if (card?.reason && te(key)) return t(key);
  return t('collector.conflicts.invoice_refused.title', { shop: card?.shopName ?? '' });
}

function toast(text) {
  try {
    f7?.toast?.create({ text, position: 'center', closeTimeout: 3500 }).open();
  } catch {
    /* no F7 app (tests, early boot): the card still says it */
  }
}

const byFiled = (a, b) => String(a.filedAt).localeCompare(String(b.filedAt));

/** A filing still owed to the server (the row's other state, `held`, is only a paper). */
const isFiling = (row) => row?.kind === PHOTO_KIND.INVOICE && row?.status === PHOTO_QUEUED && row?.filedAt != null;

/** One filing, told apart from a refile of the same shop. */
const filingId = (row) => `${row.uuid}|${row.filedAt}`;

/** ONE card per shop: a later answer about that shop replaces it. */
const cardId = (row) => `invoice:${row.planId}:${row.supplierId}`;

const isId = (value) => /^[1-9]\d*$/.test(String(value ?? ''));

/** Our API's 422 names this field (`errors.photo`, `errors["photo.0"]`). */
function names(response, field) {
  const errors = response?.data?.errors;
  if (errors == null || typeof errors !== 'object') return false;
  return Object.keys(errors).some((key) => key === field || key.startsWith(`${field}.`));
}

/** Resolve with `promise`, or with nothing once `ms` passed — the timer never outlives the race. */
function within(promise, ms) {
  let timer;
  return Promise.race([
    promise,
    new Promise((resolve) => {
      timer = setTimeout(resolve, ms);
    }),
  ]).finally(() => clearTimeout(timer));
}

let lastFiledAt = 0;

/**
 * The time of a filing, ISO with Z — and its NAME, so never the same twice and
 * never earlier than the filing it replaces (a phone's clock can be set back).
 */
function nextFiledAt(previous) {
  lastFiledAt = Math.max(Date.now(), lastFiledAt + 1, (Date.parse(previous ?? '') || 0) + 1);
  return new Date(lastFiledAt).toISOString();
}

export const useCollectorInvoicesStore = defineStore('collectorInvoices', () => {
  /** Filings still owed to the server, oldest first — plain objects (a Blob or null inside), replaced never mutated. */
  const rows = shallowRef([]);
  const flushing = ref(false);
  const started = ref(false);
  /** When the next timed retry may go (0 = no wait). */
  const nextAttemptAt = ref(0);

  /** Filings the server has not answered yet — the sync pill's count. */
  const pendingCount = computed(() => rows.value.length);

  /** What the screens show for a shop whose invoice waits on the phone. */
  const queued = computed(() =>
    rows.value.map(({ planId, supplierId, amountCents, noPaper, filedAt }) => ({ planId, supplierId, amountCents, noPaper, filedAt })),
  );

  const storage = () => collectorStorage();

  // ── Memory ─────────────────────────────────────────────────────────────────

  /** The user whose rows are in memory (undefined = nobody loaded yet). */
  let loadedFor;
  let loading = null;
  /** Filings whose write to the phone has not landed: row key → row. Put again before the next send. */
  const unsaved = new Map();
  /** Answered filings whose row could not be changed on the phone: filing → `{ uuid, change }`. Applied again before the next send. */
  const unsettled = new Map();
  /** Filings answered in this session — a stale read of the phone must not bring them back. */
  const gone = new Set();
  /** His last filing of each shop made here: row key → `filedAt`. Only that one may write over a stored filing. */
  const newest = new Map();

  /** Put `row` in memory, in filing order — unless memory already holds a later filing of that shop. */
  function keep(row) {
    if (gone.has(filingId(row))) return;
    const held = rows.value.find((other) => other.uuid === row.uuid);
    if (held && held.filedAt > row.filedAt) return;
    rows.value = [...rows.value.filter((other) => other.uuid !== row.uuid), row].sort(byFiled);
  }

  /** The filing that was answered leaves memory; a refile of the same shop made meanwhile stays. */
  function forget(sent) {
    if (unsaved.get(sent.uuid)?.filedAt === sent.filedAt) unsaved.delete(sent.uuid);
    rows.value = rows.value.filter((row) => !(row.uuid === sent.uuid && row.filedAt === sent.filedAt));
  }

  function resetMemory() {
    rows.value = [];
    unsaved.clear();
    unsettled.clear();
    gone.clear();
    newest.clear();
    owed = false;
    startRetries = 0;
    clearTimers();
  }

  /**
   * What the phone holds, into memory. A stored filing joins, or replaces the
   * one memory has of that shop when it is as late or later (a refile from the
   * other container; a new paper put on the row). A filing memory still shows
   * that the phone no longer has — the other container sent it — leaves.
   */
  function merge(stored = []) {
    const onPhone = new Map(stored.filter(isFiling).map((row) => [row.uuid, row]));
    for (const row of onPhone.values()) keep(row);
    if (rows.value.some((row) => !onPhone.has(row.uuid) && !unsaved.has(row.uuid))) {
      rows.value = rows.value.filter((row) => onPhone.has(row.uuid) || unsaved.has(row.uuid));
    }
  }

  /** The stored filings in memory before anything is sent; memoized per user, never rejects. */
  function ensureLoaded() {
    const userId = collectorUserId();
    // Nobody signed in for a moment (between a 401 and the hold): this phone's
    // collector stays in memory — it is only reset for a DIFFERENT user.
    if (userId == null && loadedFor != null) return loading ?? Promise.resolve();
    if (loading && loadedFor === userId) return loading;
    if (loadedFor !== undefined && loadedFor !== userId) resetMemory();
    loadedFor = userId;
    const work = storage()
      .loadPhotos(userId)
      .then((stored) => {
        if (loadedFor === userId) merge(stored);
        return true;
      })
      .catch((error) => {
        console.error('[collector/invoiceQueue] reading the stored filings failed', error);
        if (loading === work) loading = null; // the next call reads again
        return false;
      });
    loading = work;
    return work;
  }

  async function reconcile() {
    try {
      merge(await storage().loadPhotos(loadedFor ?? null));
    } catch (error) {
      console.warn('[collector/invoiceQueue] reconcile with the stored filings failed', error);
    }
  }

  // ── Filing (write first) ───────────────────────────────────────────────────

  /**
   * One filing onto its shop's row, in one transaction. Whatever the row held
   * — a paper, an older filing — it now carries this one; a paper already on
   * the row is kept when this filing brings none.
   *
   * Against a filing the OTHER container put there, unknown to this memory:
   *   - `fresh` (he is filing right now): this one is his last word. It always
   *     lands, named just after the stored one even when the phone's clock
   *     says otherwise — as long as it is still his last filing of that shop
   *     HERE when the transaction runs (a write retried after a lost
   *     connection can run after his next filing's).
   *   - a write put again later (it failed, or was slow): a stored filing that
   *     is later stays, and is taken into memory instead.
   * Resolves with the row as the phone now has it; with nothing on a failure —
   * the row then stays in `unsaved` (logged) and is put again before the next send.
   * (`change` reads `gone` when the transaction runs, not when it was asked for.)
   */
  async function writeRow(row, { fresh = false } = {}) {
    let stored;
    try {
      stored = await storage().patchPhoto(row.uuid, (held) => {
        // Answered while this write was on its way (a slow disk, a reopened
        // connection): an answered filing is never put back on the phone.
        if (gone.has(filingId(row))) return undefined;
        const blob = row.blob ?? held?.blob ?? null;
        if (!isFiling(held) || held.filedAt < row.filedAt) return { ...row, blob };
        const lastWord = fresh && newest.get(row.uuid) === row.filedAt;
        if (!lastWord) return held.filedAt === row.filedAt ? { ...row, blob } : undefined;
        const filedAt = filedJustAfter(held.filedAt);
        return { ...row, blob, filedAt, createdAt: filedAt };
      });
    } catch (error) {
      console.error('[collector/invoiceQueue] a filing was not written to the phone — kept in memory', error);
      return undefined;
    }
    if (unsaved.get(row.uuid) === row) unsaved.delete(row.uuid);
    if (isFiling(stored)) {
      lastFiledAt = Math.max(lastFiledAt, Date.parse(stored.filedAt) || 0);
      if (newest.get(row.uuid) === row.filedAt) newest.set(row.uuid, stored.filedAt);
      keep(stored);
    }
    return stored;
  }

  /**
   * Put on the phone what only memory holds: first the answers whose row could
   * not be changed (a filing already answered must not look unsent after a
   * restart), then the filings whose write failed. Before every send.
   */
  async function writeUnsaved() {
    for (const [id, { uuid, change }] of [...unsettled]) {
      try {
        await storage().patchPhoto(uuid, change);
        unsettled.delete(id);
      } catch (error) {
        console.warn('[collector/invoiceQueue] an answered filing is still on the phone', error);
      }
    }
    for (const row of [...unsaved.values()]) await writeRow(row);
  }

  /**
   * File the shop's invoice ON THE PHONE: ONE row per shop, a second filing
   * replaces the first. Resolves with the filing once the row is on disk, or
   * after WRITE_WAIT_MS — a slow or failed write never holds the collector at
   * the counter: the filing shows and is sent from memory, is logged, and is
   * written again before the next send. Then a send is asked for.
   *
   * Rejects only for a caller's bug (no plan, no shop, no amount): nothing is
   * kept then, rather than a row the server would answer with a 422.
   *
   * @param {{ planId: string|number, supplierId: string|number, amountCents?: number|null, noPaper?: boolean, blob?: Blob|null }} input
   * @returns {Promise<{ planId: string, supplierId: string, amountCents: number|null, noPaper: boolean, filedAt: string }>}
   */
  async function file({ planId, supplierId, amountCents = null, noPaper = false, blob = null }) {
    if (!isId(planId) || !isId(supplierId)) {
      throw new TypeError(`collector invoice: bad plan "${planId}" or shop "${supplierId}"`);
    }
    if (!noPaper && !(Number.isInteger(amountCents) && amountCents >= 0)) {
      throw new TypeError(`collector invoice: bad amount "${amountCents}"`);
    }
    // Never held at the counter by a phone slow to answer: the write below
    // settles against whatever the row holds, known to this memory or not.
    await within(ensureLoaded(), WRITE_WAIT_MS);

    const uuid = invoiceRowKey(planId, supplierId);
    const previous = unsaved.get(uuid) ?? rows.value.find((row) => row.uuid === uuid) ?? null;
    const filedAt = nextFiledAt(previous?.filedAt);
    const row = {
      uuid,
      planId: String(planId),
      lineId: null,
      supplierId: String(supplierId),
      userId: loadedFor ?? null,
      kind: PHOTO_KIND.INVOICE,
      blob: blob ?? previous?.blob ?? null,
      status: PHOTO_QUEUED,
      amountCents: noPaper ? null : amountCents,
      noPaper: Boolean(noPaper),
      filedAt,
      attempts: 0,
      lastError: null,
      createdAt: filedAt,
    };

    unsaved.set(uuid, row);
    newest.set(uuid, filedAt);
    const stored = await within(writeRow(row, { fresh: true }), WRITE_WAIT_MS);
    // Shown only now — on the phone, or the wait ran out, or the write failed.
    // (A write that landed already put the stored row in memory, paper included.)
    const filed = isFiling(stored) ? stored : row;
    if (!rows.value.some((held) => held.uuid === uuid && held.filedAt >= filed.filedAt)) keep(filed);

    flush({ reason: 'file' });
    return {
      planId: filed.planId,
      supplierId: filed.supplierId,
      amountCents: filed.amountCents,
      noPaper: filed.noPaper,
      filedAt: filed.filedAt,
    };
  }

  // ── Answers ────────────────────────────────────────────────────────────────

  /** The shop as the day has it at answer time: the day on screen, else that plan's stored day. */
  async function shopOf(row) {
    const onScreen = useCollectorDayStore().day;
    let doc = onScreen && sameId(onScreen.id, row.planId) ? onScreen : null;
    if (!doc) {
      try {
        const stored = await storage().load(loadedFor ?? null);
        doc = stored.days.find((record) => sameId(record.planId, row.planId))?.doc ?? null;
      } catch {
        /* no name: the card still opens the shop by its key */
      }
    }
    return (doc?.shops ?? []).find((shop) => sameId(shop.supplierId, row.supplierId)) ?? null;
  }

  /**
   * ONE card on the day page for this shop's filing (same collection and same
   * dismiss as the conflict cards). Resolves true when it is on the phone.
   * A refusal is also toasted, once, like a refused tap.
   */
  async function announce(row, state, reason) {
    const syncedAt = new Date().toISOString();
    const shop = await shopOf(row);
    if (state === INVOICE_CARD.REFUSED) {
      const { t, te } = i18n.global;
      toast(refusedInvoiceTitle({ reason, shopName: shop?.name ?? t('collector.shops.noShop') }, t, te));
    }
    return useCollectorActionsStore().addCard({
      id: cardId(row),
      planId: row.planId,
      state,
      reason,
      type: null,
      planItemId: null,
      shopName: shop?.name ?? null,
      shopKey: shopKey(shop ?? { supplierId: row.supplierId }),
      amountCents: row.amountCents,
      noPaper: row.noPaper,
      syncedAt,
      seenAt: syncedAt,
    });
  }

  /**
   * The answered filing leaves its row — ONLY if the row still carries the
   * filing that was sent (checked inside the transaction). `keepPaper`: a row
   * with a paper goes back to `held`, so the sheet gives it back; otherwise the
   * row is deleted. `onPhone: false` (its card could not be written) leaves the
   * row as it is: after a restart the filing is sent and judged again, rather
   * than a refusal lost with nothing to show for it.
   */
  async function settle(sent, { keepPaper = false, reason = null, onPhone = true } = {}) {
    gone.add(filingId(sent));
    if (onPhone) {
      const change = (stored) => {
        if (!isFiling(stored) || stored.filedAt !== sent.filedAt) return undefined; // he filed again meanwhile
        if (keepPaper && stored.blob != null) {
          return { status: PHOTO_HELD, amountCents: null, noPaper: false, filedAt: null, attempts: 0, lastError: reason };
        }
        return null;
      };
      try {
        await storage().patchPhoto(sent.uuid, change);
      } catch (error) {
        unsettled.set(filingId(sent), { uuid: sent.uuid, change });
        console.error('[collector/invoiceQueue] an answered filing could not be changed on the phone', error);
      }
    }
    forget(sent);
    return 'answered';
  }

  /** Filed for him: the row goes, its paper with it — and so does a card about an older filing of this shop. */
  async function applied(row) {
    const actions = useCollectorActionsStore();
    const stale = cardId(row);
    if (actions.conflicts.some((card) => card.id === stale)) actions.dismissConflict(stale);
    return settle(row);
  }

  /** The leader already filed or closed that basket: her figure stands, he is told once. */
  async function leaderClosed(row) {
    const onPhone = await announce(row, INVOICE_CARD.LEADER_CLOSED, null);
    return settle(row, { onPhone });
  }

  /** The server will not take this filing: told once, with what he had filed; the paper is kept for the next try. */
  async function refuse(row, reason, { dropPaper = false } = {}) {
    const onPhone = await announce(row, INVOICE_CARD.REFUSED, reason);
    return settle(row, { keepPaper: !dropPaper, reason, onPhone });
  }

  /** The session belongs to the collector whose filings these are (a plain profile read). */
  async function sameCollectorSignedIn() {
    try {
      return normalizeUserId(await probeSession()) === (loadedFor ?? null);
    } catch {
      return false;
    }
  }

  /**
   * One filing to the server:
   *   'answered'  a verdict — the filing left the queue
   *   'down'      no response at all: nothing else will get through either
   *   'skip'      an HTTP answer that is not a verdict on this filing: kept
   *   'paused'    401 / 419 — the login
   *   'locked'    our 403 while another account is signed in on this phone
   * Online is fed from the outcome like the other two queues': any HTTP answer
   * = our server answers; no response = it does not.
   */
  async function sendRow(row) {
    const network = collectorNetwork();
    let answer;
    try {
      answer = await postCollectorShopInvoice({
        planId: row.planId,
        supplierId: row.supplierId,
        amountCents: row.amountCents,
        noPaper: row.noPaper,
        photo: row.blob ?? null,
      });
    } catch (error) {
      const response = error?.response;
      const status = response?.status;
      if (!status) {
        network.report(false);
        return 'down';
      }
      network.report(true);
      if (status === 401 || status === 419) return 'paused';
      // Only our API's own JSON may judge a filing. A proxy's page, a gateway's
      // 413 or a captive portal's 403 is the network talking: keep it.
      if (!isOurApi(response)) return 'skip';
      if (status === 404) return refuse(row, 'not_found');
      // A paper the server cannot read is not kept for the next try.
      if (status === 422) return refuse(row, 'invalid', { dropPaper: names(response, 'photo') });
      if (status === 403) {
        // A different account signed in on this phone (the tabs share cookies)
        // is a login to put right, not a verdict on his filing.
        if (!(await sameCollectorSignedIn())) return 'locked';
        return refuse(row, 'not_collector');
      }
      return 'skip'; // a 5xx, 408, 429, anything else: a deploy, a throttle
    }

    network.report(true);
    const result = answer?.result;
    // A 200 that is not our answer (a captive portal's page) must never empty the queue.
    if (!result) return 'skip';
    if (result === 'applied' || result === 'already_applied') return applied(row);
    if (result === 'conflict' && answer.state === 'leader_closed') return leaderClosed(row);
    // refused — or an answer this app does not know: it leaves the queue and is said, never retried silently.
    return refuse(row, answer.reason ?? answer.state ?? 'error');
  }

  // ── The flush engine ───────────────────────────────────────────────────────

  let failures = 0;
  let backoffTimer = null;
  let lockTimer = null;
  let tickTimer = null;
  let holdingDetector = false;
  let again = false;
  /** A send that could go at once was held back by his marks: the taps draining sends it, whatever the backoff says. */
  let owed = false;
  let startTimer = null;
  let startRetries = 0;

  function clearTimers() {
    clearTimeout(backoffTimer);
    clearTimeout(lockTimer);
    clearTimeout(startTimer);
    backoffTimer = null;
    lockTimer = null;
    startTimer = null;
  }

  /**
   * The phone could not be read when the page started (IndexedDB can be slow
   * to open after a phone restart): a filing it holds would otherwise wait,
   * unseen, for the next visibility change. Asked again a few times.
   */
  function retryStart() {
    if (startTimer || startRetries >= START_RETRIES) return;
    startRetries += 1;
    startTimer = setTimeout(() => {
      startTimer = null;
      flush({ reason: 'start' });
    }, LOCK_RETRY_MS);
  }

  function scheduleBackoff() {
    failures += 1;
    const delay = BACKOFF_MS[Math.min(failures, BACKOFF_MS.length) - 1];
    nextAttemptAt.value = Date.now() + delay;
    clearTimeout(backoffTimer);
    backoffTimer = setTimeout(() => {
      backoffTimer = null;
      flush({ reason: 'retry' });
    }, delay);
  }

  function resetBackoff() {
    failures = 0;
    nextAttemptAt.value = 0;
    clearTimeout(backoffTimer);
    backoffTimer = null;
  }

  /** Run `work` holding the one invoice lock across tabs; false when another tab holds it. */
  async function withLock(work) {
    const locks = typeof navigator === 'undefined' ? null : navigator.locks;
    if (typeof locks?.request !== 'function') {
      await work();
      return true;
    }
    let ran = false;
    await locks.request(INVOICE_LOCK, { ifAvailable: true }, async (lock) => {
      if (!lock) return;
      ran = true;
      await work();
    });
    return ran;
  }

  let probing = false;

  /**
   * Ask whether the session holds (a plain profile read): the interceptors'
   * CSRF retry swallows the bus event on a 419 → 401 send, so a paused send
   * asks, as the action layer does. A 401 reaches the shared handler, which
   * holds the collector on his day; an answer resumes the queue.
   */
  async function probeAuth() {
    if (probing) return;
    probing = true;
    try {
      await probeSession();
      collectorNetwork().report(true);
      await sessionAlive();
    } catch (error) {
      collectorNetwork().report(error?.response?.status != null);
    } finally {
      probing = false;
    }
  }

  /**
   * Send what waits: one filing at a time, oldest first, inside one reload
   * hold. The phone is read again before EVERY send — a refile or a new paper
   * from the other container is what goes, and a tap it wrote goes first.
   * Resolves false when a filing was kept for a retry.
   */
  async function sendRounds() {
    const actions = useCollectorActionsStore();
    const release = usePwa().holdReload('collector-invoice');
    /** Each filing is sent at most once per flush, whatever its answer left behind. */
    const tried = new Set();
    let answered = false;
    let clean = true;
    let skipped = false;
    let backedOff = false;
    try {
      for (;;) {
        if (authPaused.value) break;
        await writeUnsaved();
        await reconcile();
        if (authPaused.value) break;
        // His marks first, wherever they were made — asked LAST, right before
        // the send. When one waits, its flush draining starts this again, and
        // that send is owed at once.
        if (await actions.tapsWaiting()) {
          owed = true;
          break;
        }
        const row = rows.value.find((held) => !tried.has(filingId(held)));
        if (!row) break;
        tried.add(filingId(row));

        const outcome = await sendRow(row);
        if (outcome === 'answered') {
          answered = true;
          resetBackoff();
          continue;
        }
        clean = false;
        if (outcome === 'skip') {
          // The server answered, but not about this filing (a 5xx, a page in
          // the way): it waits for its retry — the shops filed after it still go.
          skipped = true;
          continue;
        }
        // 'down', or the login. Backed off on a login too: a 419 that keeps
        // coming back on a live session must not loop pause → probe → resume → send.
        scheduleBackoff();
        backedOff = true;
        if (outcome === 'paused' || outcome === 'locked') {
          pauseForAuth();
          if (outcome === 'paused') probeAuth();
        }
        break;
      }
    } finally {
      release();
    }
    if (skipped && !backedOff) scheduleBackoff();
    // The shop now reads as the server has it (done, or the leader's figure).
    if (answered) useCollectorDayStore().refresh();
    return clean;
  }

  /**
   * Send what waits — after his marks, and with the login alive. `reason`
   * says how patient the attempt is. The 60 s tick, a resume and the taps
   * draining wait out the backoff: after a 419 on a live session the probe
   * resumes the queue and the action layer's own resume then "drains" at once —
   * without the wait that would loop pause → probe → resume → send. A filing,
   * the online flip, visibility, pageshow and the backoff's own timer try at
   * once; when his marks hold one of those back, the taps draining owes it.
   */
  async function flush({ reason = 'file' } = {}) {
    if ((await ensureLoaded()) === false && reason === 'start') retryStart();
    const actions = useCollectorActionsStore();
    // His marks go first: the taps this phone kept are in memory before the
    // cheap check below (the phone itself is asked again before every send).
    await actions.ensureHydrated();
    if (rows.value.length === 0 && unsaved.size === 0 && unsettled.size === 0) {
      if (!PEEK_REASONS.has(reason)) return;
      await reconcile();
      if (rows.value.length === 0) return;
    }
    if (authPaused.value) {
      // Nothing is sent while the login is out. The action layer asks the
      // session on visibility, pageshow and the online flip; its own tick runs
      // only while a tap waits — so this one asks when none does.
      if (reason === 'tick' && actions.pendingCount === 0) probeAuth();
      return;
    }
    const patient = reason === 'tick' || reason === 'resume' || reason === 'drained';
    if (actions.pendingCount > 0 || actions.flushing) {
      if (!patient) owed = true;
      return;
    }
    if (flushing.value) {
      again = true;
      return;
    }
    if (patient && !owed && Date.now() < nextAttemptAt.value) return;
    owed = false;

    flushing.value = true;
    let clean = true;
    try {
      const ran = await withLock(async () => {
        do {
          again = false;
          clean = await sendRounds();
        } while (again && clean);
      });
      if (!ran && !lockTimer) {
        lockTimer = setTimeout(() => {
          lockTimer = null;
          flush({ reason: 'lock' });
        }, LOCK_RETRY_MS);
      }
    } catch (error) {
      clean = false;
      console.error('[collector/invoiceQueue] sending the filings failed', error);
    } finally {
      flushing.value = false;
    }
    // Asked for between the last round and the lock's release: not lost.
    if (again && clean) {
      again = false;
      flush({ reason: 'again' });
    }
  }

  // ── Triggers ───────────────────────────────────────────────────────────────

  function onVisibility() {
    if (typeof document === 'undefined' || document.visibilityState === 'visible') flush({ reason: 'visible' });
  }

  function onPageShow() {
    flush({ reason: 'pageshow' });
  }

  function keepDetector(waiting) {
    if (waiting && !holdingDetector) {
      collectorNetwork().start();
      holdingDetector = true;
    } else if (!waiting && holdingDetector) {
      collectorNetwork().stop();
      holdingDetector = false;
    }
    if (waiting && !tickTimer) {
      tickTimer = setInterval(() => flush({ reason: 'tick' }), TICK_MS);
    } else if (!waiting && tickTimer) {
      clearInterval(tickTimer);
      tickTimer = null;
    }
  }

  function unlisten() {
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility);
    if (typeof window !== 'undefined') window.removeEventListener('pageshow', onPageShow);
  }

  /**
   * Arm the page triggers (idempotent) — called from the action layer's
   * `start()` next to the photos' one, so App.vue's boot and every collector
   * page arm the three queues together. The first call sends what the phone kept.
   */
  function start() {
    if (started.value) return;
    started.value = true;
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibility);
    if (typeof window !== 'undefined') window.addEventListener('pageshow', onPageShow);
    flush({ reason: 'start' });
  }

  const actionsStore = useCollectorActionsStore();

  // Every tap sent AND the flush that sent them finished → the filings go.
  // (`flushing` matters: the count reaches 0 while that flush is still running.)
  watch(
    () => [actionsStore.pendingCount, actionsStore.flushing],
    ([waiting, busy]) => {
      if (waiting === 0 && !busy) flush({ reason: 'drained' });
    },
  );

  // The login is back → send what waited.
  watch(authPaused, (paused, wasPaused) => {
    if (!paused && wasPaused) flush({ reason: 'resume' });
  });

  // Back online → send what waited.
  watch(collectorNetwork().isOnline, (online) => {
    if (online) flush({ reason: 'online' });
  });

  // While a filing waits: keep the shared detector running and tick every 60 s.
  watch([() => rows.value.length, started], ([waiting, armed]) => keepDetector(armed && waiting > 0), { immediate: true });

  /** Forget everything in memory (tests; another account on this phone) — the phone's storage stays. */
  function clear() {
    resetMemory();
    resetBackoff();
    loading = null;
    loadedFor = undefined;
    keepDetector(false);
    unlisten();
    started.value = false;
  }

  // The hold asks what memory holds (a write may not be on disk yet).
  registerLocalWork(
    (userId) => loadedFor === normalizeUserId(userId) && (rows.value.length > 0 || unsaved.size > 0),
    'invoices',
  );

  onScopeDispose(() => {
    clearTimers();
    keepDetector(false);
    unlisten();
  });

  return {
    rows,
    queued,
    pendingCount,
    flushing,
    started,
    nextAttemptAt,
    ensureLoaded,
    file,
    flush,
    start,
    clear,
  };
});
